<!-- Downloadable document row (icon, name, size/type) for a non-image chat attachment. -->

<script lang="ts" setup>
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { FileText } from 'lucide-vue-next';
import { computed } from 'vue';

import { formatFileSize } from '@sigma/utils/format/formatFileSize.js';

import { parseFileName } from '../utils/parseFileName.js';
import * as l from './ChatMessageDocumentRow.loc.js';

interface Props {
  fileName?: string | null;
  fileType?: string | null;
  url?: string | null;
  size?: number | null;
  color?: 'blue' | 'white';
}

const props = defineProps<Props>();

const t = useTranslate();

const parsedFileName = computed(() => parseFileName(props.fileName));

const metaLine = computed(() => {
  const parts: string[] = [];
  const formattedSize = formatFileSize(props.size);
  if (formattedSize) {
    parts.push(formattedSize);
  }
  if (parsedFileName.value.extension) {
    parts.push(parsedFileName.value.extension.toUpperCase());
  }
  return parts.join(' · ');
});
</script>

<template>
  <a
    tid="file-preview"
    :href="url ?? undefined"
    target="_blank"
    rel="noopener noreferrer"
    :aria-label="fileName || t(l.downloadFile)"
    :class="[css.row, color && css[`color_${color}`]]"
  >
    <div :class="css.iconBox">
      <FileText
        :class="css.icon"
        :size="20"
        :stroke-width="1.75"
      />
    </div>

    <div :class="css.text">
      <div :class="css.name">{{ fileName }}</div>
      <div
        v-if="metaLine"
        :class="css.meta"
      >
        {{ metaLine }}
      </div>
    </div>
  </a>
</template>

<style lang="scss" module="css">
.row {
  --doc-row-bg: #{$color-white};
  --doc-row-border: #{$color-gray-100};
  --doc-row-border-hover: #{$color-gray-300};
  --doc-row-icon-bg: #{$color-doctor-50};
  --doc-row-icon-color: #{$color-doctor-500};
  --doc-row-meta-color: #{$color-gray-500};

  display: flex;
  align-items: center;
  gap: $spacing-12;
  // Extra right padding balances the icon's visual weight on the left and gives
  // the truncated filename breathing room before the tile edge.
  padding: $spacing-12 $spacing-20 $spacing-12 $spacing-12;
  border-radius: $border-radius-12;
  text-decoration: none;
  color: inherit;
  background: var(--doc-row-bg);
  border: 1px solid var(--doc-row-border);
  transition: border-color 150ms ease-out;
  min-width: 0;

  &:hover {
    border-color: var(--doc-row-border-hover);
  }
}

.color {
  &_blue {
    --doc-row-border: #{$color-doctor-100};
    --doc-row-border-hover: #{$color-doctor-300};
  }
}

.iconBox {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: $border-radius-08;
  background: var(--doc-row-icon-bg);
  color: var(--doc-row-icon-color);
}

.icon {
  display: block;
}

.text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: $spacing-02;
}

.name {
  font-size: $font-size-14;
  font-weight: 500;
  line-height: 1.3;
  color: $color-text;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta {
  font-size: $font-size-12;
  line-height: 1.3;
  color: var(--doc-row-meta-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
